/**
 * Breadcrumbs Component
 * Dynamic breadcrumb navigation based on current route
 * Pattern: Home > Projects > [Project Name] > Knowledge Base
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { cn } from '@/lib/utils';
import { projectTabLabel } from '@/lib/projectTabLabels';
import { useProjects } from '@/hooks/useProjects';

interface BreadcrumbItem {
  label: string;
  href?: string;
  current?: boolean;
}

const MAX_BREADCRUMB_LEVELS = 4;

export const Breadcrumbs: React.FC = () => {
  const location = useLocation();
  const { projects } = useProjects();

  /**
   * Generate breadcrumb items based on current route
   */
  const getBreadcrumbs = (): BreadcrumbItem[] => {
    const breadcrumbs: BreadcrumbItem[] = [];
    const pathSegments = location.pathname.split('/').filter(Boolean);

    // Always start with Dashboard
    if (pathSegments.length === 0 || pathSegments[0] === 'dashboard') {
      breadcrumbs.push({
        label: 'Dashboard',
        href: '/dashboard',
        current: pathSegments.length === 0 || pathSegments[0] === 'dashboard',
      });
      return breadcrumbs;
    }

    // Home link
    breadcrumbs.push({
      label: 'Dashboard',
      href: '/dashboard',
    });

    // Project detail page. NOTE: this component renders in the AppLayout
    // topbar, OUTSIDE the /project/:projectId route, so useParams() is empty
    // here — derive the id from the pathname instead.
    if (pathSegments[0] === 'project' && pathSegments[1]) {
      const projectId = pathSegments[1];
      const project = projects.find((p) => p.id === projectId);
      const projectName = project?.name || 'Project';

      breadcrumbs.push({
        label: projectName,
        href: `/project/${projectId}`,
        current: pathSegments.length === 2,
      });

      // Add tab if present in search params — labels come from the canonical
      // project tab registry so breadcrumbs always match the tab bar.
      const searchParams = new URLSearchParams(location.search);
      const tab = searchParams.get('tab');
      if (tab) {
        breadcrumbs.push({
          label: projectTabLabel(tab),
          current: true,
        });
      }
    }

    // Projects CIM Analysis view
    if (pathSegments[0] === 'projects' && pathSegments[2] === 'cim' && pathSegments[3]) {
      const projectId = pathSegments[1];
      const project = projects.find((p) => p.id === projectId);
      const projectName = project?.name || 'Project';

      breadcrumbs.push({
        label: 'Projects',
        href: '/dashboard',
      });

      breadcrumbs.push({
        label: projectName,
        href: `/project/${projectId}`,
      });

      breadcrumbs.push({
        label: 'CIM Analysis',
        current: true,
      });
    }

    // Truncate if too many levels
    if (breadcrumbs.length > MAX_BREADCRUMB_LEVELS) {
      breadcrumbs.splice(1, breadcrumbs.length - MAX_BREADCRUMB_LEVELS, {
        label: '...',
      });
    }

    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  // Don't show breadcrumbs on login/signup pages
  if (location.pathname === '/login' || location.pathname === '/signup') {
    return null;
  }

  return (
    <nav aria-label="Breadcrumb" className="flex items-center space-x-1 text-sm">
      {breadcrumbs.map((item, index) => {
        const isLast = index === breadcrumbs.length - 1;
        const isTruncated = item.label === '...';

        return (
          <React.Fragment key={index}>
            {index > 0 && (
              <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            )}

            {isTruncated ? (
              <span className="text-muted-foreground">...</span>
            ) : item.href && (!isLast || index === 0) ? (
              <Link
                to={item.href}
                className={cn(
                  'transition-colors font-medium flex items-center gap-1',
                  isLast || item.current
                    ? 'text-foreground hover:text-muted-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {index === 0 && <Home className="h-3.5 w-3.5" />}
                {item.label}
              </Link>
            ) : (
              <span
                className={cn(
                  'font-medium flex items-center gap-1',
                  isLast || item.current
                    ? 'text-foreground'
                    : 'text-muted-foreground'
                )}
              >
                {index === 0 && <Home className="h-3.5 w-3.5" />}
                {item.label}
              </span>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};
